<script setup lang="ts">
/* =============================================================
   BERTHS — LIST
   =============================================================
   Identity, place and owner: name, city, country, company. The
   coordinates and the amenities are on the detail page, where
   there is room for them and where they are not competing with
   the name for a glance.

   Opening a berth is the only thing a row does — in either
   view. Edit and delete live on the detail page, so there is no
   way to destroy a row from a list where you can only see four
   of its fields.

   The view lives in `?view=map` rather than in component state
   or localStorage, so a map view can be linked to and survives
   a reload. No query means the table, which is the default.

   The filter is client-side, which is right for now and not
   forever: the ports spreadsheet this will be filled from is
   about 2000 rows, and around that size an `ilike` filter and a
   server-side page want to replace it.
   ============================================================= */

import { computed, defineAsyncComponent, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import Button from '@/components/ui/Button.vue'
import Alert from '@/components/ui/Alert.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import FilterInput from '@/components/ui/FilterInput.vue'
import Icon from '@/components/Icon.vue'
import type { MapPoint } from '@/lib/map'

/* Leaflet and its stylesheet are ~150 kB that the table view never touches,
   and the table is the default — so the map arrives in its own chunk the
   first time someone asks for it. The type import above is erased at build
   time and does not pull the module in. */
const MapView = defineAsyncComponent(() => import('@/components/MapView.vue'))
import { routes, to } from '@/config/routes'
import { berthComparator, listBerths } from '@/lib/berths'
import { useCountries } from '@/lib/countries'
import { DataError } from '@/lib/errors'
import type { Berth } from '@/lib/types'

const route = useRoute()
const router = useRouter()
const { countryName } = useCountries()

type View = 'table' | 'map'
const view = computed<View>(() => (route.query.view === 'map' ? 'map' : 'table'))

function setView(next: View): void {
  const query = { ...route.query }
  if (next === 'table') delete query.view
  else query.view = 'map'
  void router.replace({ query })
}

const rows = ref<Berth[]>([])
const loading = ref(true)
const loadError = ref('')
const filter = ref('')

const sorted = computed(() => [...rows.value].sort(berthComparator(countryName)))

const visible = computed(() => {
  const q = filter.value.trim().toLowerCase()
  if (!q) return sorted.value
  return sorted.value.filter(
    (r) =>
      r.name.toLowerCase().includes(q) ||
      r.city.toLowerCase().includes(q) ||
      countryName(r.country_code).toLowerCase().includes(q) ||
      (r.company?.name.toLowerCase().includes(q) ?? false),
  )
})

const countLabel = computed(() => {
  const total = rows.value.length
  const shown = visible.value.length
  const noun = total === 1 ? 'berth' : 'berths'
  return shown === total ? `${total} ${noun}` : `${shown} of ${total} ${noun}`
})

/** The filtered rows, as something the map service understands. */
const points = computed<MapPoint[]>(() =>
  visible.value.map((b) => ({
    id: b.id,
    lat: b.latitude,
    lng: b.longitude,
    title: b.name,
    subtitle: `${b.city}, ${countryName(b.country_code)}`,
  })),
)

async function load(): Promise<void> {
  loading.value = true
  loadError.value = ''
  try {
    rows.value = await listBerths()
  } catch (e) {
    loadError.value = e instanceof DataError ? e.message : 'Could not load berths.'
  } finally {
    loading.value = false
  }
}

onMounted(load)

/**
 * The whole row is clickable, but the name is a real link and does the work —
 * this only forwards a click that landed on some other cell. It bails if text
 * is selected, so dragging across a row to copy a name does not navigate.
 */
function openRow(berth: Berth, event: MouseEvent): void {
  if ((event.target as HTMLElement).closest('a')) return
  if (window.getSelection()?.toString()) return
  void router.push(to.berth(berth.id))
}

/** A marker goes exactly where its row does. */
function openPoint(point: MapPoint): void {
  void router.push(to.berth(Number(point.id)))
}
</script>

<template>
  <PageHeader title="Berths" description="Docking positions on the platform.">
    <template #actions>
      <Button variant="ghost" icon="refresh" :loading="loading" @click="load">Refresh</Button>
      <Button icon="plus" :to="routes.berthNew">New berth</Button>
    </template>
  </PageHeader>

  <Alert v-if="loadError" tone="danger" class="page-alert">{{ loadError }}</Alert>

  <Panel flush :title="rows.length > 0 ? countLabel : undefined">
    <template v-if="rows.length > 0" #actions>
      <div class="views" role="group" aria-label="View">
        <button
          type="button"
          class="viewBtn"
          :class="{ viewBtnOn: view === 'table' }"
          :aria-pressed="view === 'table'"
          @click="setView('table')"
        >
          <Icon name="dashboard" :size="15" />
          Table
        </button>
        <button
          type="button"
          class="viewBtn"
          :class="{ viewBtnOn: view === 'map' }"
          :aria-pressed="view === 'map'"
          @click="setView('map')"
        >
          <Icon name="pin" :size="15" />
          Map
        </button>
      </div>

      <FilterInput
        v-model="filter"
        label="Filter berths by name, city, country or owner"
        placeholder="Filter by name, city, country or owner"
      />
    </template>

    <p v-if="loading && rows.length === 0" class="panel-status">Loading…</p>

    <EmptyState
      v-else-if="rows.length === 0"
      icon="anchor"
      title="No berths yet"
      description="Add the first docking position, or import the ports spreadsheet once bulk upload exists."
    >
      <template #action>
        <Button icon="plus" :to="routes.berthNew">New berth</Button>
      </template>
    </EmptyState>

    <p v-else-if="visible.length === 0" class="panel-status">No berth matches “{{ filter }}”.</p>

    <MapView
      v-else-if="view === 'map'"
      :points="points"
      height="34rem"
      @select="openPoint"
    />

    <div v-else class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Owner</th>
            <th scope="col">City</th>
            <th scope="col">Country</th>
            <th scope="col"><span class="sr-only">Open</span></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="berth in visible" :key="berth.id" class="rowLink" @click="openRow(berth, $event)">
            <td class="cell-strong">
              <RouterLink :to="to.berth(berth.id)" class="nameLink">{{ berth.name }}</RouterLink>
            </td>
            <td class="cell-muted">
              <RouterLink v-if="berth.company" :to="to.company(berth.company.id)" class="nameLink">
                {{ berth.company.name }}
              </RouterLink>
            </td>
            <td class="cell-muted">{{ berth.city }}</td>
            <td class="cell-muted">{{ countryName(berth.country_code) }}</td>
            <td class="cell-actions">
              <Icon name="chevronRight" :size="16" class="chevron" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </Panel>
</template>

<style scoped>
.views {
  display: flex;
  gap: 2px;
  padding: 2px;
  background-color: var(--color-surface-sunken);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-sm);
}

.viewBtn {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3xs);
  padding: 0 var(--space-2xs);
  height: calc(var(--control-height-sm) - 6px);
  font-family: inherit;
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  color: var(--color-text-muted);
  background: none;
  border: 0;
  border-radius: calc(var(--radius-sm) - 2px);
  transition:
    color var(--dur-fast) var(--ease),
    background-color var(--dur-fast) var(--ease);
}

.viewBtn:hover {
  color: var(--color-heading);
}

/* aria-pressed carries the state for assistive tech; this is the visual half
   of the same fact. */
.viewBtnOn,
.viewBtnOn:hover {
  color: var(--color-accent-hover);
  background-color: var(--color-surface);
  box-shadow: var(--shadow-sm);
}
</style>
